/**
 * Support Service - Client-side management of support requests
 */

import { apiBase } from "../api-config";
import type {
  SupportRequest,
  SupportRequestDetail,
  SupportResponse,
  CreateSupportRequestRequest,
  AddSupportResponseRequest,
  ResolveSupportRequestRequest,
} from "../../types/support";

const API_BASE = apiBase;

class SupportService {
  // ============================================
  // Patient Endpoints
  // ============================================

  /**
   * Create a new support request (patient side)
   */
  async createRequest(
    request: CreateSupportRequestRequest,
  ): Promise<SupportRequest> {
    const response = await fetch(`${API_BASE}/api/support/request`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Failed to create support request");
    }

    return response.json();
  }

  /**
   * Get all support requests for the current patient
   */
  async getMyRequests(): Promise<SupportRequest[]> {
    const response = await fetch(`${API_BASE}/api/support/my-requests`, {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch support requests");
    }

    return response.json();
  }

  /**
   * Get a specific support request with all responses
   */
  async getRequestDetail(requestId: string): Promise<SupportRequestDetail> {
    const response = await fetch(
      `${API_BASE}/api/support/request/${requestId}`,
      {
        method: "GET",
        credentials: "include",
        headers: { Accept: "application/json" },
      },
    );

    if (!response.ok) {
      throw new Error("Failed to fetch support request");
    }

    return response.json();
  }

  // ============================================
  // Clinician Endpoints
  // ============================================

  /**
   * Get all open support requests for a clinic (clinician side)
   */
  async getClinicRequests(clinicGroupId: string): Promise<SupportRequest[]> {
    const response = await fetch(
      `${API_BASE}/api/support/clinic/${clinicGroupId}/requests`,
      {
        method: "GET",
        credentials: "include",
        headers: { Accept: "application/json" },
      },
    );

    if (!response.ok) {
      throw new Error("Failed to fetch clinic support requests");
    }

    return response.json();
  }

  /**
   * Add a response to a support request (clinician side)
   */
  async addResponse(
    requestId: string,
    request: AddSupportResponseRequest,
  ): Promise<SupportResponse> {
    const response = await fetch(
      `${API_BASE}/api/support/request/${requestId}/respond`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Failed to add response");
    }

    return response.json();
  }

  /**
   * Resolve a support request (clinician side)
   */
  async resolveRequest(
    requestId: string,
    request?: ResolveSupportRequestRequest,
  ): Promise<SupportRequest> {
    const response = await fetch(
      `${API_BASE}/api/support/request/${requestId}/resolve`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(request || {}),
      },
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Failed to resolve request");
    }

    return response.json();
  }

  /**
   * Assign a request to oneself (clinician side)
   */
  async assignRequest(requestId: string): Promise<SupportRequest> {
    const response = await fetch(
      `${API_BASE}/api/support/request/${requestId}/assign`,
      {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json" },
      },
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Failed to assign request");
    }

    return response.json();
  }
}

// Export singleton instance
export const supportService = new SupportService();
